import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { katalogApi } from '../api/katalogApi';
import { textbausteinApi } from '../api/textbausteinApi';
import apiClient from '../api/apiClient';
import Layout from '../components/Layout/Layout';
import toast from 'react-hot-toast';
import {
  Plus,
  Search,
  Edit2,
  Trash2,
  Download,
  Upload,
  X,
  Package,
  ChevronLeft,
  ChevronRight,
  Wrench,
  Truck,
  FileText,
  Pencil,
  Save,
  CheckSquare,
  Square,
  MinusSquare,
  ToggleLeft,
  ToggleRight
} from '../icons/wp-icons';
import FahrzeugeTab from '../components/Katalog/FahrzeugeTab';
import WerkzeugeTab from '../components/Katalog/WerkzeugeTab';

const KATEGORIEN = [
  'Glas',
  'Rahmen',
  'Montage',
  'Fahrt',
  'Material',
  'Bauteile',
  'Kleinteile/Zubehör',
  'Dichtungen/Kleber',
  'Werkzeug/Geräte',
  'Arbeitszeit',
  'Fahrtkosten',
  'Entsorgung',
  'Fremdleistung',
  'Sonstiges'
];
const EINHEITEN = ['Stk', 'm²', 'm', 'Std', 'Pauschale'];

// Textbausteine Konstanten
const TB_KATEGORIEN = ['EINLEITUNG', 'SCHLUSSTEXT', 'ZAHLUNGSBEDINGUNG', 'HINWEIS'];
const TB_LABELS = { EINLEITUNG: 'Einleitung', SCHLUSSTEXT: 'Schlusstext', ZAHLUNGSBEDINGUNG: 'Zahlungsbedingung', HINWEIS: 'Hinweis' };
const TB_COLORS = { EINLEITUNG: 'bg-blue-100 text-blue-800', SCHLUSSTEXT: 'bg-purple-100 text-purple-800', ZAHLUNGSBEDINGUNG: 'bg-amber-100 text-amber-800', HINWEIS: 'bg-green-100 text-green-800' };

export default function Katalog() {
  const [searchParams, setSearchParams] = useSearchParams();
  const VALID_TABS = ['positionen', 'textbausteine', 'fahrzeuge', 'werkzeuge'];
  const rawTab = searchParams.get('tab');
  const activeTab = VALID_TABS.includes(rawTab) ? rawTab : 'positionen';
  const setActiveTab = (tab) => setSearchParams(tab === 'positionen' ? {} : { tab });

  const [positionen, setPositionen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editPosition, setEditPosition] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);

  // Textbausteine State
  const [textbausteine, setTextbausteine] = useState([]);
  const [tbLoading, setTbLoading] = useState(false);
  const [showTbModal, setShowTbModal] = useState(false);
  const [editTb, setEditTb] = useState(null);
  const [tbFilterKategorie, setTbFilterKategorie] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  // Bulk Selection
  const [selectedPositionen, setSelectedPositionen] = useState(new Set());
  const [selectedTextbausteine, setSelectedTextbausteine] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);

  // Filter
  const [searchTerm, setSearchTerm] = useState('');
  const [filterKategorie, setFilterKategorie] = useState('');
  const [filterAktiv, setFilterAktiv] = useState('true');

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  useEffect(() => {
    loadPositionen();
  }, [filterKategorie, filterAktiv]);

  // Textbausteine beim Mount laden (für Counter) + bei Tab-Wechsel/Filter
  useEffect(() => {
    loadTextbausteine();
    setSelectedTextbausteine(new Set());
  }, [tbFilterKategorie]);

  const loadTextbausteine = async () => {
    try {
      setTbLoading(true);
      const data = await textbausteinApi.getAll(tbFilterKategorie || undefined);
      setTextbausteine(data);
    } catch (error) {
      console.error('Fehler beim Laden:', error);
    } finally {
      setTbLoading(false);
    }
  };

  const handleDeleteTb = async (id) => {
    try {
      await textbausteinApi.delete(id);
      setDeleteConfirmId(null);
      toast.success('Textbaustein gelöscht');
      await loadTextbausteine();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const loadPositionen = async () => {
    try {
      setLoading(true);
      const filters = {};
      if (filterKategorie) filters.kategorie = filterKategorie;
      if (filterAktiv !== '') filters.aktiv = filterAktiv;
      if (searchTerm) filters.search = searchTerm;

      const data = await katalogApi.getPositionen(filters);
      setPositionen(data);
    } catch (error) {
      console.error('Fehler beim Laden:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadPositionen();
  };

  const handleDelete = async (id) => {
    if (!confirm('Position wirklich löschen?')) return;

    try {
      await katalogApi.deletePosition(id);
      await loadPositionen();
    } catch (error) {
      alert('Fehler beim Löschen');
    }
  };

  const handleExport = async () => {
    try {
      const response = await apiClient.get('/katalog/export/csv', {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'katalog-export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert('Fehler beim Export: ' + error.message);
    }
  };

  // Bulk-Aktionen Positionen
  const togglePositionSelection = (id) => {
    setSelectedPositionen(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllPositionen = () => {
    if (selectedPositionen.size === paginatedPositionen.length) {
      setSelectedPositionen(new Set());
    } else {
      setSelectedPositionen(new Set(paginatedPositionen.map(p => p.id)));
    }
  };

  const handleBulkPositionen = async (action) => {
    const ids = Array.from(selectedPositionen);
    const labels = { delete: 'löschen', activate: 'aktivieren', deactivate: 'deaktivieren' };
    if (!confirm(`${ids.length} Position(en) wirklich ${labels[action]}?`)) return;

    setBulkLoading(true);
    try {
      const result = await katalogApi.bulkAction(ids, action);
      toast.success(result.message);
      setSelectedPositionen(new Set());
      await loadPositionen();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Fehler bei Bulk-Aktion');
    } finally {
      setBulkLoading(false);
    }
  };

  // Bulk-Aktionen Textbausteine
  const toggleTextbausteinSelection = (id) => {
    setSelectedTextbausteine(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllTextbausteine = () => {
    if (selectedTextbausteine.size === textbausteine.length) {
      setSelectedTextbausteine(new Set());
    } else {
      setSelectedTextbausteine(new Set(textbausteine.map(t => t.id)));
    }
  };

  const handleBulkTextbausteine = async (action) => {
    const ids = Array.from(selectedTextbausteine);
    if (!confirm(`${ids.length} Textbaustein(e) wirklich löschen?`)) return;

    setBulkLoading(true);
    try {
      const result = await textbausteinApi.bulkAction(ids, action);
      toast.success(result.message);
      setSelectedTextbausteine(new Set());
      await loadTextbausteine();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Fehler bei Bulk-Aktion');
    } finally {
      setBulkLoading(false);
    }
  };

  const filteredPositionen = positionen.filter(pos => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      pos.bezeichnung?.toLowerCase().includes(search) ||
      pos.artikelnummer?.toLowerCase().includes(search) ||
      pos.beschreibung?.toLowerCase().includes(search)
    );
  });

  // Reset auf Seite 1 und Selection wenn Filter sich ändern
  useEffect(() => {
    setCurrentPage(1);
    setSelectedPositionen(new Set());
  }, [searchTerm, filterKategorie, filterAktiv]);

  // Pagination Berechnung
  const totalPages = Math.ceil(filteredPositionen.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedPositionen = filteredPositionen.slice(startIndex, endIndex);

  // Statistiken
  const stats = {
    gesamt: positionen.length,
    aktiv: positionen.filter(p => p.aktiv).length,
    gesamtwert: positionen.reduce((sum, p) => sum + (p.verkaufspreis * (p.aktiv ? 1 : 0)), 0)
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8 overflow-x-hidden">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 sm:mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-serif font-bold text-gray-900">Katalog & Vorlagen</h1>
            <p className="text-gray-600 text-sm sm:text-base mt-1">
              <span className="hidden sm:inline">Positionen und Textbausteine verwalten</span>
              <span className="sm:hidden">{
                activeTab === 'positionen' ? `${stats.gesamt} Positionen`
                : activeTab === 'textbausteine' ? `${textbausteine.length} Textbausteine`
                : activeTab === 'fahrzeuge' ? 'Fahrzeuge'
                : 'Werkzeuge'
              }</span>
            </p>
            {/* Mobile: Button unter Titel — nur für Positionen/Textbausteine.
                Fahrzeuge/Werkzeuge bringen ihre eigenen Buttons in der Tab-Komponente mit. */}
            {activeTab === 'positionen' && (
              <button
                onClick={() => { setEditPosition(null); setShowModal(true); }}
                className="flex sm:hidden items-center gap-2 bg-primary text-white px-4 py-2 rounded-button font-medium hover:bg-primary-dark transition mt-3"
              >
                <Plus className="w-5 h-5" />
                Neue Position
              </button>
            )}
            {activeTab === 'textbausteine' && (
              <button
                onClick={() => { setEditTb(null); setShowTbModal(true); }}
                className="flex sm:hidden items-center gap-2 bg-primary text-white px-4 py-2 rounded-button font-medium hover:bg-primary-dark transition mt-3"
              >
                <Plus className="w-5 h-5" />
                Neuer Textbaustein
              </button>
            )}
          </div>

          <div className="hidden sm:flex gap-2 sm:gap-3">
            {activeTab === 'positionen' && (
              <>
                <button onClick={handleExport} className="flex items-center gap-2 bg-surface border border-border text-text px-4 py-2 rounded-button font-medium hover:bg-gray-50 transition">
                  <Upload className="w-4 h-4" /> CSV Export
                </button>
                <button onClick={() => setShowImportModal(true)} className="flex items-center gap-2 bg-surface border border-border text-text px-4 py-2 rounded-button font-medium hover:bg-gray-50 transition">
                  <Download className="w-4 h-4" /> CSV Import
                </button>
                <button onClick={() => { setEditPosition(null); setShowModal(true); }} className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-button font-medium hover:bg-primary-dark transition">
                  <Plus className="w-5 h-5" /> Neue Position
                </button>
              </>
            )}
            {activeTab === 'textbausteine' && (
              <button onClick={() => { setEditTb(null); setShowTbModal(true); }} className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-button font-medium hover:bg-primary-dark transition">
                <Plus className="w-5 h-5" /> Neuer Textbaustein
              </button>
            )}
          </div>
        </div>

        {/* Tab Bar */}
        <div className="flex border-b border-gray-200 mb-6">
          <button
            onClick={() => setActiveTab('positionen')}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition ${
              activeTab === 'positionen'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Package className="w-4 h-4" />
            Positionen
            <span className="px-1.5 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">{positionen.length}</span>
          </button>
          <button
            onClick={() => setActiveTab('textbausteine')}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition ${
              activeTab === 'textbausteine'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <FileText className="w-4 h-4" />
            Textbausteine
            <span className="px-1.5 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">{textbausteine.length}</span>
          </button>
          <button
            onClick={() => setActiveTab('fahrzeuge')}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition ${
              activeTab === 'fahrzeuge'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Truck className="w-4 h-4" />
            Fahrzeuge
          </button>
          <button
            onClick={() => setActiveTab('werkzeuge')}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition ${
              activeTab === 'werkzeuge'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Wrench className="w-4 h-4" />
            Werkzeuge
          </button>
        </div>

        {activeTab === 'fahrzeuge' && <FahrzeugeTab />}
        {activeTab === 'werkzeuge' && <WerkzeugeTab />}

        {activeTab === 'positionen' && (<>
        {/* Statistiken - nur auf Desktop */}
        <div className="hidden sm:grid grid-cols-3 gap-4 sm:gap-6 mb-6">
          <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="p-2 sm:p-3 bg-blue-100 rounded-lg">
                <Package className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-xs sm:text-sm text-gray-600">Gesamt</p>
                <p className="text-xl sm:text-2xl font-bold text-gray-900">{stats.gesamt}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="p-2 sm:p-3 bg-green-100 rounded-lg">
                <Package className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
              </div>
              <div>
                <p className="text-xs sm:text-sm text-gray-600">Aktiv</p>
                <p className="text-xl sm:text-2xl font-bold text-gray-900">{stats.aktiv}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="p-2 sm:p-3 bg-purple-100 rounded-lg">
                <Package className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-xs sm:text-sm text-gray-600">Ø Preis</p>
                <p className="text-xl sm:text-2xl font-bold text-gray-900">
                  {stats.aktiv > 0 ? (stats.gesamtwert / stats.aktiv).toFixed(2) : '0.00'}€
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Filter & Suche */}
        <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 mb-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
            <div className="col-span-2">
              <form onSubmit={handleSearch} className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Suche..."
                  className="w-full pl-9 sm:pl-10 pr-4 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </form>
            </div>

            <div>
              <select
                value={filterKategorie}
                onChange={(e) => setFilterKategorie(e.target.value)}
                className="w-full px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="">Kategorie</option>
                {KATEGORIEN.map(kat => (
                  <option key={kat} value={kat}>{kat}</option>
                ))}
              </select>
            </div>

            <div>
              <select
                value={filterAktiv}
                onChange={(e) => setFilterAktiv(e.target.value)}
                className="w-full px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="">Status</option>
                <option value="true">Aktive</option>
                <option value="false">Inaktive</option>
              </select>
            </div>
          </div>
        </div>

        {/* Bulk Action Bar */}
        {selectedPositionen.size > 0 && (
          <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 sm:p-4 mb-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <CheckSquare className="w-5 h-5 text-primary" />
              <span className="text-sm font-medium text-gray-900">
                {selectedPositionen.size} Position(en) ausgewählt
              </span>
            </div>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <button
                onClick={() => handleBulkPositionen('activate')}
                disabled={bulkLoading}
                className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition disabled:opacity-50"
              >
                <ToggleRight className="w-4 h-4" />
                Aktivieren
              </button>
              <button
                onClick={() => handleBulkPositionen('deactivate')}
                disabled={bulkLoading}
                className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 transition disabled:opacity-50"
              >
                <ToggleLeft className="w-4 h-4" />
                Deaktivieren
              </button>
              <button
                onClick={() => handleBulkPositionen('delete')}
                disabled={bulkLoading}
                className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition disabled:opacity-50"
              >
                <Trash2 className="w-4 h-4" />
                Löschen
              </button>
              <button
                onClick={() => setSelectedPositionen(new Set())}
                className="p-2 text-gray-400 hover:text-gray-600 transition"
                title="Auswahl aufheben"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Positionen Liste */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            </div>
          ) : filteredPositionen.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              Keine Positionen gefunden
            </div>
          ) : (
            <>
              {/* Mobile Card-Ansicht */}
              <div className="sm:hidden divide-y divide-gray-200">
                {paginatedPositionen.map((pos) => {
                  const marge = pos.einkaufspreis
                    ? ((pos.verkaufspreis - pos.einkaufspreis) / pos.verkaufspreis * 100).toFixed(1)
                    : null;

                  return (
                    <div key={pos.id} className={`p-4 ${selectedPositionen.has(pos.id) ? 'bg-primary/5' : ''}`}>
                      <div className="flex items-start justify-between gap-3">
                        <button
                          onClick={() => togglePositionSelection(pos.id)}
                          className="mt-0.5 shrink-0 text-gray-400 hover:text-primary transition"
                        >
                          {selectedPositionen.has(pos.id)
                            ? <CheckSquare className="w-5 h-5 text-primary" />
                            : <Square className="w-5 h-5" />
                          }
                        </button>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-gray-900 truncate">{pos.bezeichnung}</span>
                            {pos.istLohn && (
                              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700 shrink-0" title="Lohnleistung">
                                <Wrench className="w-3 h-3" />
                                L
                              </span>
                            )}
                            <span className={`px-1.5 py-0.5 text-xs font-medium rounded-full shrink-0 ${
                              pos.aktiv ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                            }`}>
                              {pos.aktiv ? 'Aktiv' : 'Inaktiv'}
                            </span>
                          </div>
                          <div className="flex flex-wrap items-center gap-2 text-sm text-gray-500">
                            <span className="px-2 py-0.5 bg-gray-100 rounded-full text-xs">{pos.kategorie}</span>
                            {pos.artikelnummer && <span className="text-xs">{pos.artikelnummer}</span>}
                            {pos.defaultLieferant && (
                              <span className="px-2 py-0.5 bg-amber-50 text-amber-700 rounded-full text-xs">{pos.defaultLieferant.name}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <button
                            onClick={() => {
                              setEditPosition(pos);
                              setShowModal(true);
                            }}
                            className="p-2 text-primary hover:bg-gray-100 rounded-lg transition"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(pos.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                        <div className="text-sm">
                          <span className="font-medium text-gray-900">{pos.verkaufspreis.toFixed(2)}€</span>
                          <span className="text-gray-500">/{pos.einheit}</span>
                        </div>
                        {marge && (
                          <span className={`text-sm font-medium ${marge > 30 ? 'text-green-600' : marge > 15 ? 'text-yellow-600' : 'text-red-600'}`}>
                            {marge}% Marge
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Desktop Tabellen-Ansicht */}
              <table className="w-full hidden sm:table">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 w-10">
                      <button onClick={toggleAllPositionen} className="text-gray-400 hover:text-primary transition">
                        {paginatedPositionen.length > 0 && selectedPositionen.size === paginatedPositionen.length
                          ? <CheckSquare className="w-5 h-5 text-primary" />
                          : selectedPositionen.size > 0
                            ? <MinusSquare className="w-5 h-5 text-primary" />
                            : <Square className="w-5 h-5" />
                        }
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Artikel-Nr.
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Bezeichnung
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Kategorie
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Einheit
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      VK-Preis
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      EK-Preis
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Marge
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Aktionen
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedPositionen.map((pos) => {
                    const marge = pos.einkaufspreis
                      ? ((pos.verkaufspreis - pos.einkaufspreis) / pos.verkaufspreis * 100).toFixed(1)
                      : null;

                    return (
                      <tr key={pos.id} className={`hover:bg-gray-50 transition ${selectedPositionen.has(pos.id) ? 'bg-primary/5' : ''}`}>
                        <td className="px-4 py-4 w-10">
                          <button onClick={() => togglePositionSelection(pos.id)} className="text-gray-400 hover:text-primary transition">
                            {selectedPositionen.has(pos.id)
                              ? <CheckSquare className="w-5 h-5 text-primary" />
                              : <Square className="w-5 h-5" />
                            }
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {pos.artikelnummer || '-'}
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900 flex items-center gap-1.5">
                            {pos.bezeichnung}
                            {pos.istLohn && (
                              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700 shrink-0" title="Lohnleistung">
                                <Wrench className="w-3 h-3" />
                                L
                              </span>
                            )}
                          </div>
                          {pos.beschreibung && (
                            <div className="text-sm text-gray-500 truncate max-w-xs">
                              {pos.beschreibung}
                            </div>
                          )}
                          {pos.defaultLieferant && (
                            <span className="inline-flex items-center mt-1 px-2 py-0.5 bg-amber-50 text-amber-700 rounded-full text-xs">
                              {pos.defaultLieferant.name}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                            {pos.kategorie}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {pos.einheit}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {pos.verkaufspreis.toFixed(2)}€
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {pos.einkaufspreis ? `${pos.einkaufspreis.toFixed(2)}€` : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {marge ? (
                            <span className={`font-medium ${marge > 30 ? 'text-green-600' : marge > 15 ? 'text-yellow-600' : 'text-red-600'}`}>
                              {marge}%
                            </span>
                          ) : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            pos.aktiv
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {pos.aktiv ? 'Aktiv' : 'Inaktiv'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => {
                                setEditPosition(pos);
                                setShowModal(true);
                              }}
                              className="text-primary hover:text-primary-dark transition"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(pos.id)}
                              className="text-red-600 hover:text-red-800 transition"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 sm:px-6 py-4 border-t border-gray-200">
              <div className="text-sm text-gray-500">
                Zeige {startIndex + 1} - {Math.min(endIndex, filteredPositionen.length)} von {filteredPositionen.length} Positionen
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="flex items-center gap-1 px-2 sm:px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  <ChevronLeft className="w-4 h-4" />
                  <span className="hidden sm:inline">Zurück</span>
                </button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(page => {
                      return page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1;
                    })
                    .map((page, idx, arr) => (
                      <span key={page} className="flex items-center">
                        {idx > 0 && arr[idx - 1] !== page - 1 && (
                          <span className="px-1 sm:px-2 text-gray-400">...</span>
                        )}
                        <button
                          onClick={() => setCurrentPage(page)}
                          className={`px-2 sm:px-3 py-2 text-sm rounded-lg transition ${
                            currentPage === page
                              ? 'bg-primary text-white'
                              : 'hover:bg-gray-50 border border-gray-300'
                          }`}
                        >
                          {page}
                        </button>
                      </span>
                    ))}
                </div>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="flex items-center gap-1 px-2 sm:px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  <span className="hidden sm:inline">Weiter</span>
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>

        </>)}

        {/* === TEXTBAUSTEINE TAB === */}
        {activeTab === 'textbausteine' && (
          <>
            {/* Filter */}
            <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 mb-6">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-gray-400 hidden sm:block" />
                <select
                  value={tbFilterKategorie}
                  onChange={(e) => setTbFilterKategorie(e.target.value)}
                  className="w-full sm:w-64 px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="">Alle Kategorien</option>
                  {TB_KATEGORIEN.map(kat => (
                    <option key={kat} value={kat}>{TB_LABELS[kat]}</option>
                  ))}
                </select>
                <span className="text-sm text-gray-500 hidden sm:inline">
                  {textbausteine.length} Einträge
                </span>
                {textbausteine.length > 0 && (
                  <button
                    onClick={toggleAllTextbausteine}
                    className="ml-auto flex items-center gap-1.5 text-sm text-gray-500 hover:text-primary transition"
                  >
                    {selectedTextbausteine.size === textbausteine.length
                      ? <><CheckSquare className="w-4 h-4 text-primary" /> Alle abwählen</>
                      : <><Square className="w-4 h-4" /> Alle auswählen</>
                    }
                  </button>
                )}
              </div>
            </div>

            {/* Bulk Action Bar Textbausteine */}
            {selectedTextbausteine.size > 0 && (
              <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 sm:p-4 mb-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <CheckSquare className="w-5 h-5 text-primary" />
                  <span className="text-sm font-medium text-gray-900">
                    {selectedTextbausteine.size} Textbaustein(e) ausgewählt
                  </span>
                </div>
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <button
                    onClick={() => handleBulkTextbausteine('delete')}
                    disabled={bulkLoading}
                    className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition disabled:opacity-50"
                  >
                    <Trash2 className="w-4 h-4" />
                    Löschen
                  </button>
                  <button
                    onClick={() => setSelectedTextbausteine(new Set())}
                    className="p-2 text-gray-400 hover:text-gray-600 transition"
                    title="Auswahl aufheben"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {/* Content */}
            {tbLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
              </div>
            ) : textbausteine.length === 0 ? (
              <div className="bg-white rounded-xl shadow-sm p-12 text-center">
                <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg mb-2">Keine Textbausteine vorhanden</p>
                <p className="text-gray-400 text-sm mb-6">
                  {tbFilterKategorie
                    ? `Keine Textbausteine in "${TB_LABELS[tbFilterKategorie]}" gefunden.`
                    : 'Erstellen Sie Ihren ersten Textbaustein.'}
                </p>
                <button
                  onClick={() => { setEditTb(null); setShowTbModal(true); }}
                  className="inline-flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-button font-medium hover:bg-primary-dark transition"
                >
                  <Plus className="w-5 h-5" /> Textbaustein erstellen
                </button>
              </div>
            ) : (
              <div className="space-y-6">
                {(() => {
                  const grouped = TB_KATEGORIEN.reduce((acc, kat) => {
                    const items = textbausteine.filter(tb => tb.kategorie === kat);
                    if (items.length > 0) acc[kat] = items;
                    return acc;
                  }, {});
                  const displayKats = tbFilterKategorie
                    ? [tbFilterKategorie].filter(k => grouped[k])
                    : TB_KATEGORIEN.filter(k => grouped[k]);

                  return displayKats.map(kat => (
                    <div key={kat}>
                      <div className="flex items-center gap-3 mb-3">
                        <span className={`px-3 py-1 text-sm font-medium rounded-full ${TB_COLORS[kat]}`}>
                          {TB_LABELS[kat]}
                        </span>
                        <span className="text-sm text-gray-400">{grouped[kat]?.length || 0} Einträge</span>
                      </div>
                      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                        <div className="divide-y divide-gray-200">
                          {grouped[kat]?.map((tb) => (
                            <div key={tb.id} className={`p-4 sm:p-5 hover:bg-gray-50 transition ${selectedTextbausteine.has(tb.id) ? 'bg-primary/5' : ''}`}>
                              <div className="flex items-start justify-between gap-3">
                                <button
                                  onClick={() => toggleTextbausteinSelection(tb.id)}
                                  className="mt-0.5 shrink-0 text-gray-400 hover:text-primary transition"
                                >
                                  {selectedTextbausteine.has(tb.id)
                                    ? <CheckSquare className="w-5 h-5 text-primary" />
                                    : <Square className="w-5 h-5" />
                                  }
                                </button>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                                    <span className="font-medium text-gray-900">{tb.name}</span>
                                    {tb.istStandard && (
                                      <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-primary/10 text-primary">Standard</span>
                                    )}
                                  </div>
                                  <p className="text-sm text-gray-500 line-clamp-2">
                                    {tb.inhalt?.length > 100 ? tb.inhalt.substring(0, 100) + '...' : tb.inhalt}
                                  </p>
                                </div>
                                <div className="flex items-center gap-1 sm:gap-2 shrink-0">
                                  <button
                                    onClick={() => { setEditTb(tb); setShowTbModal(true); }}
                                    className="p-2 text-primary hover:bg-gray-100 rounded-lg transition"
                                    title="Bearbeiten"
                                  >
                                    <Pencil className="w-4 h-4" />
                                  </button>
                                  {deleteConfirmId === tb.id ? (
                                    <div className="flex items-center gap-1">
                                      <button onClick={() => handleDeleteTb(tb.id)} className="p-2 text-white bg-red-600 hover:bg-red-700 rounded-lg transition"><Trash2 className="w-4 h-4" /></button>
                                      <button onClick={() => setDeleteConfirmId(null)} className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition"><X className="w-4 h-4" /></button>
                                    </div>
                                  ) : (
                                    <button onClick={() => setDeleteConfirmId(tb.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"><Trash2 className="w-4 h-4" /></button>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ));
                })()}
              </div>
            )}
          </>
        )}

        {/* Modal für Erstellen/Bearbeiten */}
        <AnimatePresence>
          {showModal && (
            <KatalogModal
              position={editPosition}
              onClose={() => {
                setShowModal(false);
                setEditPosition(null);
              }}
              onSuccess={() => {
                setShowModal(false);
                setEditPosition(null);
                loadPositionen();
              }}
            />
          )}
        </AnimatePresence>

        {/* Import Modal */}
        <AnimatePresence>
          {showImportModal && (
            <ImportModal
              onClose={() => setShowImportModal(false)}
              onSuccess={() => {
                setShowImportModal(false);
                loadPositionen();
              }}
            />
          )}
        </AnimatePresence>

        {/* Textbaustein Modal */}
        <AnimatePresence>
          {showTbModal && (
            <TextbausteinModal
              textbaustein={editTb}
              onClose={() => { setShowTbModal(false); setEditTb(null); }}
              onSuccess={() => { setShowTbModal(false); setEditTb(null); loadTextbausteine(); }}
            />
          )}
        </AnimatePresence>
      </div>
    </Layout>
  );
}

// Katalog Modal Komponente - Edit/Create Position
function KatalogModal({ position, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    artikelnummer: position?.artikelnummer || '',
    bezeichnung: position?.bezeichnung || '',
    beschreibung: position?.beschreibung || '',
    kategorie: position?.kategorie || 'Glas',
    einheit: position?.einheit || 'Stk',
    verkaufspreis: position?.verkaufspreis || '',
    einkaufspreis: position?.einkaufspreis || '',
    mwstSatz: position?.mwstSatz || 19.0,
    istLohn: position?.istLohn || false,
    aktiv: position?.aktiv !== undefined ? position.aktiv : true,
    defaultLieferantId: position?.defaultLieferantId || ''
  });

  const [loading, setLoading] = useState(false);
  const [lieferanten, setLieferanten] = useState([]);

  useEffect(() => {
    const loadLieferanten = async () => {
      try {
        const { getAllKunden } = await import('../api/kundenApi');
        const data = await getAllKunden({ status: 'lieferant' });
        setLieferanten(data.filter(k => k.status === 'lieferant'));
      } catch (err) {
        console.error('Lieferanten laden fehlgeschlagen:', err);
      }
    };
    loadLieferanten();
  }, []);

  // Escape-Taste zum Schließen
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (position) {
        await katalogApi.updatePosition(position.id, formData);
      } else {
        await katalogApi.createPosition(formData);
      }
      onSuccess();
    } catch (error) {
      alert('Fehler: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-0 sm:p-4 overflow-y-auto">
      <motion.div
        className="fixed inset-0 bg-black bg-opacity-50"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15, ease: "easeOut" }}
        onClick={onClose}
      />
      <motion.div
        className="bg-white sm:rounded-2xl p-4 sm:p-6 max-w-2xl w-full min-h-screen sm:min-h-0 sm:max-h-[90vh] sm:overflow-y-auto relative z-10"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{
          duration: 0.15,
          ease: "easeOut",
          exit: { duration: 0.1, ease: "easeIn" }
        }}>
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
            {position ? 'Position bearbeiten' : 'Neue Position'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {/* Artikelnummer */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Artikelnummer (optional)
              </label>
              <input
                type="text"
                value={formData.artikelnummer}
                onChange={(e) => setFormData({...formData, artikelnummer: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="z.B. GLX-001"
              />
            </div>

            {/* Kategorie */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Kategorie *
              </label>
              <select
                value={formData.kategorie}
                onChange={(e) => setFormData({...formData, kategorie: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                required
              >
                {KATEGORIEN.map(kat => (
                  <option key={kat} value={kat}>{kat}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Bezeichnung */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Bezeichnung *
            </label>
            <input
              type="text"
              value={formData.bezeichnung}
              onChange={(e) => setFormData({...formData, bezeichnung: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="z.B. Isolierglas 4mm"
              required
            />
          </div>

          {/* Beschreibung */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Beschreibung (optional)
            </label>
            <textarea
              value={formData.beschreibung}
              onChange={(e) => setFormData({...formData, beschreibung: e.target.value})}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="Zusätzliche Details..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Einheit */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Einheit *
              </label>
              <select
                value={formData.einheit}
                onChange={(e) => setFormData({...formData, einheit: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                required
              >
                {EINHEITEN.map(einh => (
                  <option key={einh} value={einh}>{einh}</option>
                ))}
              </select>
            </div>

            {/* MwSt-Satz */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                MwSt-Satz *
              </label>
              <select
                value={formData.mwstSatz}
                onChange={(e) => setFormData({...formData, mwstSatz: parseFloat(e.target.value)})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                required
              >
                <option value={7.0}>7%</option>
                <option value={19.0}>19%</option>
              </select>
            </div>
          </div>

          {/* Lohnleistung */}
          <div className="flex items-center gap-3">
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData.istLohn}
                onChange={(e) => setFormData({...formData, istLohn: e.target.checked})}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
            </label>
            <div>
              <span className="text-sm font-medium text-gray-700">Lohnleistung</span>
              <p className="text-xs text-gray-500">Position wird als Arbeitsleistung markiert</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Verkaufspreis */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Verkaufspreis (Netto) *
              </label>
              <div className="relative">
                <input
                  type="number"
                  step="0.01"
                  value={formData.verkaufspreis}
                  onChange={(e) => setFormData({...formData, verkaufspreis: e.target.value})}
                  className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="0.00"
                  required
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">€</span>
              </div>
            </div>

            {/* Einkaufspreis */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Einkaufspreis (optional)
              </label>
              <div className="relative">
                <input
                  type="number"
                  step="0.01"
                  value={formData.einkaufspreis}
                  onChange={(e) => setFormData({...formData, einkaufspreis: e.target.value})}
                  className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="0.00"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">€</span>
              </div>
            </div>
          </div>

          {/* Standard-Lieferant */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Standard-Lieferant (optional)
            </label>
            <select
              value={formData.defaultLieferantId}
              onChange={(e) => setFormData({...formData, defaultLieferantId: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="">– Kein Lieferant –</option>
              {lieferanten.map(l => (
                <option key={l.id} value={l.id}>{l.name}</option>
              ))}
            </select>
          </div>

          {/* Aktiv */}
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-700">
              Position aktiv
            </label>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData.aktiv}
                onChange={(e) => setFormData({...formData, aktiv: e.target.checked})}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
            </label>
          </div>

          {/* Buttons - mit extra Padding für mobile Bottom-Navigation */}
          <div className="flex gap-3 pt-4 pb-24 sm:pb-0">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 sm:px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-medium"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 sm:px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition font-medium disabled:opacity-50"
            >
              {loading ? 'Speichere...' : position ? 'Aktualisieren' : 'Erstellen'}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

// Import Modal Komponente
function ImportModal({ onClose, onSuccess }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  // Escape-Taste zum Schließen
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const handleImport = async () => {
    if (!file) {
      alert('Bitte wählen Sie eine CSV-Datei');
      return;
    }

    setLoading(true);

    try {
      const result = await katalogApi.importCSV(file);
      setResult(result);

      if (result.fehler === 0) {
        setTimeout(() => {
          onSuccess();
        }, 2000);
      }
    } catch (error) {
      alert('Import-Fehler: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <motion.div
        className="fixed inset-0 bg-black bg-opacity-50"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15, ease: "easeOut" }}
        onClick={onClose}
      />
      <motion.div
        className="bg-white rounded-2xl p-6 max-w-2xl w-full relative z-10"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{
          duration: 0.15,
          ease: "easeOut",
          exit: { duration: 0.1, ease: "easeIn" }
        }}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900">CSV Import</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="space-y-4">
          {/* Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-900">
              <strong>CSV-Format:</strong> Artikelnummer; Bezeichnung; Beschreibung; Kategorie; Einheit; Verkaufspreis; Einkaufspreis; MwSt-Satz; Aktiv
            </p>
            <p className="text-sm text-blue-900 mt-2">
              Bei vorhandener Artikelnummer wird die Position aktualisiert, sonst neu erstellt.
            </p>
          </div>

          {/* File Upload */}
          {!result && (
            <>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                <input
                  type="file"
                  accept=".csv"
                  onChange={(e) => setFile(e.target.files[0])}
                  className="hidden"
                  id="csv-upload"
                />
                <label htmlFor="csv-upload" className="cursor-pointer">
                  <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-lg font-medium text-gray-900 mb-1">
                    {file ? file.name : 'CSV-Datei auswählen'}
                  </p>
                  <p className="text-sm text-gray-500">
                    Oder Datei hierher ziehen
                  </p>
                </label>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-medium"
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleImport}
                  disabled={!file || loading}
                  className="flex-1 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition font-medium disabled:opacity-50"
                >
                  {loading ? 'Importiere...' : 'Import starten'}
                </button>
              </div>
            </>
          )}

          {/* Result */}
          {result && (
            <div className="space-y-4">
              <div className={`rounded-lg p-4 ${
                result.fehler === 0 ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'
              }`}>
                <p className="font-medium mb-2">{result.message}</p>
                <p className="text-sm">
                  {result.erfolg} erfolgreich importiert
                  {result.fehler > 0 && ` | ${result.fehler} Fehler`}
                </p>
              </div>

              {result.errors && result.errors.length > 0 && (
                <div className="max-h-60 overflow-y-auto bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="font-medium text-red-900 mb-2">Fehler-Details:</p>
                  <ul className="text-sm text-red-800 space-y-1">
                    {result.errors.map((err, idx) => (
                      <li key={idx}>• {err}</li>
                    ))}
                  </ul>
                </div>
              )}

              <button
                onClick={result.fehler === 0 ? onSuccess : onClose}
                className="w-full px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition font-medium"
              >
                {result.fehler === 0 ? 'Fertig' : 'Schließen'}
              </button>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}

// Textbaustein Modal Komponente
function TextbausteinModal({ textbaustein, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    name: textbaustein?.name || '',
    kategorie: textbaustein?.kategorie || 'EINLEITUNG',
    inhalt: textbaustein?.inhalt || '',
    istStandard: textbaustein?.istStandard || false,
    sortierung: textbaustein?.sortierung || 0
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const handleEscape = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (textbaustein) {
        await textbausteinApi.update(textbaustein.id, formData);
        toast.success('Textbaustein aktualisiert');
      } else {
        await textbausteinApi.create(formData);
        toast.success('Textbaustein erstellt');
      }
      onSuccess();
    } catch (error) {
      toast.error('Fehler: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-0 sm:p-4 overflow-y-auto">
      <motion.div className="fixed inset-0 bg-black bg-opacity-50" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }} onClick={onClose} />
      <motion.div className="bg-white sm:rounded-2xl p-4 sm:p-6 max-w-2xl w-full min-h-screen sm:min-h-0 sm:max-h-[90vh] sm:overflow-y-auto relative z-10" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.15, exit: { duration: 0.1 } }}>
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">{textbaustein ? 'Textbaustein bearbeiten' : 'Neuer Textbaustein'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1"><X className="w-6 h-6" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
            <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" placeholder="z.B. Standard-Einleitung Rechnung" required />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Kategorie *</label>
              <select value={formData.kategorie} onChange={(e) => setFormData({ ...formData, kategorie: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" required>
                {TB_KATEGORIEN.map(kat => <option key={kat} value={kat}>{TB_LABELS[kat]}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sortierung</label>
              <input type="number" value={formData.sortierung} onChange={(e) => setFormData({ ...formData, sortierung: parseInt(e.target.value) || 0 })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" placeholder="0" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Inhalt *</label>
            <textarea value={formData.inhalt} onChange={(e) => setFormData({ ...formData, inhalt: e.target.value })} rows={6} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" placeholder="Text eingeben..." required />
          </div>
          <div className="flex items-center gap-3">
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" checked={formData.istStandard} onChange={(e) => setFormData({ ...formData, istStandard: e.target.checked })} className="sr-only peer" />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
            </label>
            <div>
              <span className="text-sm font-medium text-gray-700">Als Standard verwenden</span>
              <p className="text-xs text-gray-500">Wird automatisch in neue Dokumente eingefügt</p>
            </div>
          </div>
          <div className="flex gap-3 pt-4 pb-24 sm:pb-0">
            <button type="button" onClick={onClose} className="flex-1 px-4 sm:px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-medium">Abbrechen</button>
            <button type="submit" disabled={loading} className="flex-1 flex items-center justify-center gap-2 px-4 sm:px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition font-medium disabled:opacity-50">
              <Save className="w-4 h-4" /> {loading ? 'Speichere...' : textbaustein ? 'Aktualisieren' : 'Erstellen'}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}
